import { CheckCircleOutlined, CheckOutlined, LeftOutlined, PlusOutlined, WarningOutlined } from "@ant-design/icons";
import { Button, Col, DatePicker, Divider, Flex, Form, Image, Input, InputNumber, Row, Select, Space, Tooltip } from "antd";
import TextArea from "antd/lib/input/TextArea";
import dayjs from "dayjs";
import { useEffect, useMemo, useRef, useState } from "react";
import { NumericFormat } from "react-number-format";
import { CondicaoGeralSorteioModel } from "../../../../models/condicaoGeralSorteio/condicaoGeralModel";
import { EmpresaModel } from "../../../../models/empresa/empresaModel";
import { GradePremiacaoModel } from "../../../../models/gradePremiacao/gradePremiacaoModel";
import { setTitle } from "../../../../redux/slices/configuracoes/configuracoesSlice";
import { fillEntityCG, fillEntityGradePremiacao, setPageSorteio } from "../../../../redux/slices/sorteio/sorteioSlice";
import { buscarCondicaoGeralSorteioPorId } from "../../../../services/condicaoGeralSorteioService";
import { buscarEmpresa } from "../../../../services/empresaService";
import { buscarGradePremiacaoPorId } from "../../../../services/gradePremiacaoService";
import { atualizarSorteio, cadastrarSorteio } from "../../../../services/sorteioService";
import { buscarTipoSorteio } from "../../../../services/tipoSorteioService";
import { currencyBRL, percentParaNumber, realParaNumber } from "../../../../util/dinheiroBRAuxiliar";
import { useAppDispatch, useAppSelector } from "../../../../util/hooks";
import { toastError, toastWarning } from "../../../../util/toast";
import "./style/index.scss";

dayjs.locale("pt-br");

const CadastrarPage = () => {
  //estados globais
  const empresaState = useAppSelector((state) => state.empresa);
  const sorteioState = useAppSelector((state) => state.sorteio);
  const condicaoGeralSorteioState = useAppSelector((state) => state.condicaoGeralSorteio);
  const gradePremiacaoState = useAppSelector((state) => state.gradePremiacao);
  const tipoSorteioState = useAppSelector((state) => state.tipoSorteio);

  //hooks
  const dispatch = useAppDispatch();

  //estados locais
  const [form] = Form.useForm<any>();
  const [addGrade, setAddGrade] = useState(false);
  const [addCG, setAddCG] = useState(false);
  const [totalValor, setTotalValor] = useState<number>(0);
  const [totalFatorTitulo, setTotalFatorTitulo] = useState<number>(0);

  const inputInicial = useRef<any>(null);
  const editarSorteio = Object.values(sorteioState.entity).length !== 0;
  const editarCG = Object.values(sorteioState.entityCG).length !== 0;
  const editarGrade = Object.values(sorteioState.entityGradePremiacao).length !== 0;

  const condicaoGeralOptions = useMemo(() => {
    const lista = condicaoGeralSorteioState.list ?? [];

    return [...lista]
      .sort((a: any, b: any) => {
        const valorA = a.numeroCG ?? "";
        const valorB = b.numeroCG ?? "";
        if (!isNaN(Number(valorA)) && !isNaN(Number(valorB))) {
          return Number(valorA) - Number(valorB);
        }
        return String(valorA).localeCompare(String(valorB), "pt-BR", { numeric: true });
      })
      .map((a: any) => ({
        value: a.id,
        label: a.numeroCG,
      }));
  }, [condicaoGeralSorteioState.list]);

  useEffect(() => {
    if (inputInicial.current && !editarCG) {
      inputInicial.current.focus();
    }
    dispatch(buscarEmpresa(empresaState.busca));
    dispatch(buscarTipoSorteio(tipoSorteioState.busca));
    //CG
    if (editarCG) {
      form.setFieldsValue({
        ...sorteioState.entityCG,
        quotaSorteio: sorteioState.entityCG.quotaSorteio * 100,
        quotaResgate: sorteioState.entityCG.quotaResgate * 100,
        quotaCarregamento: sorteioState.entityCG.quotaCarregamento * 100,
      });
    }
    //Grade
    if (editarGrade) {
      form.setFieldsValue({ ...sorteioState.entityGradePremiacao });
      handleCalculoFatorTitulo();
      handleCalculoValorTotal();
    }
    //Sorteio
    if (editarSorteio) {
      dispatch(setTitle("EDITAR SORTEIO"));
      form.setFieldsValue({
        ...sorteioState.entity,
        idCondicaoGeralSorteio: sorteioState.entity.idCondicaoGeralSorteio,
        idGradePremiacao: sorteioState.entity.idGradePremiacao,
      });
      form.setFieldsValue({
        dataSorteio: dayjs(new Date(sorteioState.entity.dataSorteio)),
      });
      form.setFieldValue("valorBonificacao", sorteioState.entity.valorBonificacao ?? null);
    } else {
      dispatch(setTitle("NOVO SORTEIO"));
      form.setFieldsValue({
        idEmpresa: sorteioState.busca.idEmpresas && parseInt(sorteioState.busca.idEmpresas),
      });
    }
  }, [condicaoGeralSorteioState.busca, sorteioState.entity]);

  useEffect(() => {
    if (editarSorteio && !addCG && sorteioState.entity.idCondicaoGeralSorteio && condicaoGeralOptions.length > 0) {
      form.setFieldValue("idCondicaoGeralSorteio", sorteioState.entity.idCondicaoGeralSorteio);
    }
  }, [editarSorteio, addCG, condicaoGeralOptions, form, sorteioState.entity.idCondicaoGeralSorteio]);

  const cadastrarCGButton = () => {
    setAddCG(true);
    form.setFieldValue("numeroProcesso", null);
    form.setFieldValue("numeroCG", 0);
    form.setFieldValue("tamanhoSerie", "0");
    form.setFieldValue("quantidadeVendaMinima", "0");
    form.setFieldValue("quotaSorteio", "0");
    form.setFieldValue("quotaResgate", "0");
    form.setFieldValue("quotaCarregamento", "0");
    form.setFieldValue("idCondicaoGeralSorteio", null);
    dispatch(fillEntityCG({} as CondicaoGeralSorteioModel));
  };

  const handleCGChange = async (value: any) => {
    const retornoCG = await dispatch(buscarCondicaoGeralSorteioPorId(value));
    dispatch(fillEntityCG({ ...retornoCG.payload }));
    form.setFieldsValue({
      ...retornoCG.payload,
      quotaSorteio: retornoCG.payload.quotaSorteio * 100,
      quotaResgate: retornoCG.payload.quotaResgate * 100,
      quotaCarregamento: retornoCG.payload.quotaCarregamento * 100,
    });
    setAddCG(false);
  };

  const cadastrarGradeButton = () => {
    setAddGrade(true);
    form.setFieldValue("idGradePremiacao", null);
    form.setFieldValue("denominacao", null);
    form.setFieldValue("valorTotalPremiacaoLiquido", "0");
    form.setFieldValue("valorTotalPremiacaoBruto", "0");
    form.setFieldValue("valorPremio1", "0");
    form.setFieldValue("valorPremio2", "0");
    form.setFieldValue("valorPremio3", "0");
    form.setFieldValue("valorPremio4", "0");
    form.setFieldValue("valorPremio5", "0");
    form.setFieldValue("valorRodada", "0");
    form.setFieldValue("fatorPremiacao", "0");
    form.setFieldValue("valorTitulo", "0");
    dispatch(fillEntityGradePremiacao({} as GradePremiacaoModel));
  };

  const handleGradeChange = async (value: any) => {
    const retornoGrade = await dispatch(buscarGradePremiacaoPorId(value));
    dispatch(fillEntityGradePremiacao({ ...retornoGrade.payload }));
    form.setFieldsValue({ ...retornoGrade.payload });
    handleCalculoFatorTitulo();
    handleCalculoValorTotal();
  };

  const validationsSubmitForm = (entity: any): boolean => {
    const somaQuotas = entity.quotaSorteio + entity.quotaCarregamento + entity.quotaResgate;
    if (somaQuotas < 0.99 || somaQuotas > 1) {
      toastWarning("Quotas devem somar 100%!");
      return false;
    }
    if (addCG) {
      var buscaNumero = condicaoGeralSorteioState.list.find((a: any) => a.numeroCG === form.getFieldValue("numeroCG"));
      if (buscaNumero !== undefined) {
        toastWarning("Número da condição geral já existe!");
        form.setFieldValue("numeroCG", "");
        return false;
      }
    }
    return true;
  };

  const submit = (values: any) => {
    const valorBonificacaoNormalizado = values.valorBonificacao === undefined || values.valorBonificacao === null || values.valorBonificacao === "" ? null : realParaNumber(values.valorBonificacao);

    values.valorBonificacao = valorBonificacaoNormalizado;

    if (Math.abs(Math.abs(totalValor) - Math.abs(totalFatorTitulo)) <= 0.01) {
      if (addCG) {
        values.quotaSorteio = percentParaNumber(form.getFieldValue("quotaSorteio"));
        values.quotaCarregamento = percentParaNumber(form.getFieldValue("quotaCarregamento"));
        values.quotaResgate = percentParaNumber(form.getFieldValue("quotaResgate"));
        const valid = validationsSubmitForm(values);
        values.quotaSorteio = values.quotaSorteio * 100;
        values.quotaCarregamento = values.quotaCarregamento * 100;
        values.quotaResgate = values.quotaResgate * 100;
        if (valid) {
          editarSorteio
            ? dispatch(atualizarSorteio({ value: { ...values, id: sorteioState.entity.id }, busca: sorteioState.busca }))
            : dispatch(cadastrarSorteio({ value: values, busca: sorteioState.busca }));
        }
      } else {
        editarSorteio
          ? dispatch(atualizarSorteio({ value: { ...values, id: sorteioState.entity.id }, busca: sorteioState.busca }))
          : dispatch(cadastrarSorteio({ value: values, busca: sorteioState.busca }));
      }
    } else {
      toastError("O valor da premiação bruta difere do fator x título em grade de premiação");
    }
  };

  const handleCalculoValorTotal = () => {
    form.setFieldValue(
      "valorTotalPremiacaoLiquido",
      form.getFieldValue("valorPremio1") +
        form.getFieldValue("valorPremio2") +
        form.getFieldValue("valorPremio3") +
        form.getFieldValue("valorPremio4") +
        form.getFieldValue("valorPremio5") +
        form.getFieldValue("valorRodada"),
    );
    form.setFieldValue("valorTotalPremiacaoBruto", Math.trunc((form.getFieldValue("valorTotalPremiacaoLiquido") / 0.75) * 100) / 100);
    setTotalValor(form.getFieldValue("valorTotalPremiacaoBruto"));
  };

  const handleCalculoFatorTitulo = () => {
    form.setFieldValue("totalFatorTitulo", form.getFieldValue("fatorPremiacao") * form.getFieldValue("valorTitulo"));
    setTotalFatorTitulo(form.getFieldValue("totalFatorTitulo"));
  };

  const voltar = () => {
    dispatch(setPageSorteio("geral"));
  };

  return (
    <Flex gap={12} vertical>
      <Flex onClick={voltar} className="voltar">
        {"<"} VOLTAR
      </Flex>
      <Form form={form} layout="vertical" colon={false} onFinish={submit}>
        <Row gutter={12}>
          <Col span={7}>
            <Form.Item
              label="Empresa"
              name="idEmpresa"
              rules={[
                {
                  required: true,
                  message: "Escolha a empresa",
                },
              ]}
            >
              <Select
                optionFilterProp="labelSearch"
                loading={empresaState.loading}
                style={{ width: "100%" }}
                options={empresaState.list.map((a: EmpresaModel) => ({
                  value: a.id,
                  key: a.apelido,
                  label: (
                    <Flex justify="flex-start" align="center" gap={8} style={{ padding: "1px 0px" }}>
                      <Flex style={{ lineHeight: "0px" }}>{a.urlLogotipoPequeno && <Image width={25} height={25} style={{ lineHeight: "0px" }} src={a.urlLogotipoPequeno} preview={false} />}</Flex>
                      {a.apelido}
                    </Flex>
                  ),
                  labelSearch: a.apelido,
                }))}
              />
            </Form.Item>
          </Col>
          <Col span={5}>
            <Form.Item
              label="Data do sorteio"
              name="dataSorteio"
              rules={[
                {
                  required: true,
                  message: "Escolha a data do sorteio",
                },
              ]}
            >
              <DatePicker format="DD/MM/YYYY" style={{ width: "100%" }} />
            </Form.Item>
          </Col>
          <Col span={5}>
            <Form.Item
              label="Número do sorteio"
              name="numeroExtracao"
              rules={[
                {
                  required: true,
                  message: "Digite o número do sorteio",
                },
              ]}
            >
              <InputNumber size="small" controls={false} className="inputNumberNumero" style={{ width: "100%" }} type="number" />
            </Form.Item>
          </Col>
          <Col span={7}>
            <Form.Item
              label="Tipo de sorteio"
              name="idTipoSorteio"
              rules={[
                {
                  required: true,
                  message: "Escolha um tipo de sorteio",
                },
              ]}
            >
              <Select
                style={{ width: "100%" }}
                loading={tipoSorteioState.loading}
                ref={inputInicial}
                options={tipoSorteioState.list.map((a: any) => ({
                  value: a.id,
                  label: a.denominacao,
                  disabled: !a.ativo,
                }))}
              />
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={12}>
          <Col span={4}>
            <Form.Item label="Valor de bonificação" name="valorBonificacao">
              <NumericFormat
                className="ant-input noBoderColor number-input"
                style={{ width: "100%" }}
                thousandSeparator="."
                decimalSeparator=","
                decimalScale={4}
                prefix="R$"
                fixedDecimalScale={true}
              />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item label="Observações" name="observacao">
              <TextArea style={{ height: "73px" }} />
            </Form.Item>
          </Col>
        </Row>
        <Divider orientation="left" orientationMargin="0">
          Condição Geral
        </Divider>
        <Row gutter={12}>
          <Col span={6}>
            <Form.Item
              label="CG"
              name="idCondicaoGeralSorteio"
              rules={[
                {
                  required: !addCG,
                  message: "Digite o número da condição geral",
                },
              ]}
            >
              <Select showSearch onChange={handleCGChange} optionFilterProp="label" placeholder="Selecione a condição geral do sorteio" options={condicaoGeralOptions} />
            </Form.Item>
          </Col>
          <Col span={6} className="addCG">
            <Form.Item label=" ">
              <Button className="buttonPrimary" onClick={cadastrarCGButton} icon={<PlusOutlined />}>
                NOVA CG
              </Button>
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={12}>
          <Col span={6}>
            <Form.Item
              label="Número CG"
              name="numeroCG"
              rules={[
                {
                  required: true,
                  message: "Digite o número da condição geral",
                },
              ]}
            >
              <InputNumber style={{ width: "100%" }} controls={false} disabled={!addCG} />
            </Form.Item>
          </Col>
          <Col span={6}>
            <Form.Item
              label="Número do Processo"
              name="numeroProcesso"
              rules={[
                {
                  required: true,
                  message: "Digite o número do processo",
                },
              ]}
            >
              <Input style={{ width: "100%" }} type="text" disabled={!addCG} />
            </Form.Item>
          </Col>
          <Col span={6}>
            <Form.Item
              label="Tamanho série"
              name="tamanhoSerie"
              rules={[
                {
                  required: true,
                  message: "Digite o tamanho da série",
                },
              ]}
            >
              <NumericFormat
                className="ant-input noBoderColor number-input"
                style={{ width: "100%" }}
                thousandSeparator="."
                decimalSeparator=","
                decimalScale={2}
                fixedDecimalScale={true}
                disabled={!addCG}
                onChange={() => form.setFieldValue("tamanhoSerie", realParaNumber(form.getFieldValue("tamanhoSerie")))}
                valueIsNumericString={false}
              />
            </Form.Item>
          </Col>
          <Col span={6}>
            <Form.Item
              label="Quantidade mínima"
              name="quantidadeVendaMinima"
              rules={[
                {
                  required: true,
                  message: "Digite a quantidade de venda mínima",
                },
              ]}
            >
              <InputNumber style={{ width: "100%" }} controls={false} disabled={!addCG} />
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={12}>
          <Col span={8}>
            <Form.Item
              label="Quota sorteio"
              name="quotaSorteio"
              rules={[
                {
                  required: true,
                  message: "Digite a quota sorteio",
                },
              ]}
            >
              <NumericFormat className="ant-input noBoderColor number-input" style={{ width: "100%" }} decimalSeparator="," decimalScale={5} suffix="%" fixedDecimalScale={true} disabled={!addCG} />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item
              label="Quota resgate (Capitalização)"
              name="quotaResgate"
              rules={[
                {
                  required: true,
                  message: "Digite a quota resgate",
                },
              ]}
            >
              <NumericFormat className="ant-input noBoderColor number-input" style={{ width: "100%" }} decimalSeparator="," decimalScale={5} suffix="%" fixedDecimalScale={true} disabled={!addCG} />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item
              label="Quota carregamento"
              name="quotaCarregamento"
              rules={[
                {
                  required: true,
                  message: "Digite a quota carregamento",
                },
              ]}
            >
              <NumericFormat className="ant-input noBoderColor number-input" style={{ width: "100%" }} decimalSeparator="," decimalScale={5} suffix="%" fixedDecimalScale={true} disabled={!addCG} />
            </Form.Item>
          </Col>
        </Row>
        <Divider orientation="left" orientationMargin="0">
          Grade de premiação
        </Divider>
        <Row gutter={12}>
          <Col span={6}>
            <Form.Item
              name="idGradePremiacao"
              label="Grade de premiação"
              rules={[
                {
                  required: !addGrade,
                  message: "Escolha a grade de premiação",
                },
              ]}
            >
              <Select
                onChange={handleGradeChange}
                placeholder="Selecione a grade de premiação"
                showSearch
                optionFilterProp="label"
                options={gradePremiacaoState.list.map((grade: any) => ({
                  value: grade.id,
                  label: `${grade.denominacao} - ${currencyBRL(grade.valorTotalPremiacaoBruto)}`,
                }))}
              />
            </Form.Item>
          </Col>
          <Col span={4}>
            <Form.Item label=" ">
              <Button className="buttonPrimary" onClick={cadastrarGradeButton} icon={<PlusOutlined />}>
                NOVA GRADE
              </Button>
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={12}>
          <Col span={8}>
            <Form.Item
              label="Denominação"
              name="denominacao"
              rules={[
                {
                  required: true,
                  message: "Digite a denominação",
                },
              ]}
            >
              <Input type="text" size="middle" disabled={!addGrade} />
            </Form.Item>
          </Col>
          <Col span={6}>
            <Form.Item
              label="Fator premiação"
              name="fatorPremiacao"
              rules={[
                {
                  required: true,
                  message: "Digite o fator premiação",
                },
              ]}
            >
              <NumericFormat
                className="ant-input noBoderColor number-input"
                style={{ width: "100%" }}
                thousandSeparator="."
                decimalSeparator=","
                decimalScale={5}
                fixedDecimalScale={true}
                disabled={!addGrade}
                onValueChange={() =>
                  setTimeout(() => {
                    form.setFieldValue("fatorPremiacao", realParaNumber(form.getFieldValue("fatorPremiacao")));
                    handleCalculoFatorTitulo();
                  }, 700)
                }
              />
            </Form.Item>
          </Col>
          <Col span={4}>
            <Form.Item
              label="Valor título (R$)"
              name="valorTitulo"
              rules={[
                {
                  required: true,
                  message: "Digite o valor do título",
                },
              ]}
            >
              <NumericFormat
                className="ant-input noBoderColor number-input"
                style={{ width: "100%" }}
                thousandSeparator="."
                decimalSeparator=","
                decimalScale={2}
                prefix="R$"
                fixedDecimalScale={true}
                disabled={!addGrade}
                onValueChange={() =>
                  setTimeout(() => {
                    form.setFieldValue("valorTitulo", realParaNumber(form.getFieldValue("valorTitulo")));
                    handleCalculoFatorTitulo();
                  }, 700)
                }
              />
            </Form.Item>
          </Col>
          <Col span={6}>
            <Form.Item
              label={
                <>
                  <span style={{ marginRight: "3px" }}>Fator x Título</span>
                  {!(Math.abs(Math.abs(totalValor) - Math.abs(totalFatorTitulo)) <= 0.01) ? (
                    <>
                      <Tooltip title="O valor da premiação bruta difere do fator x título">
                        <WarningOutlined style={{ color: "#FF0000" }} />
                      </Tooltip>
                    </>
                  ) : (
                    <>
                      <CheckCircleOutlined style={{ color: "#008000", marginLeft: "3px" }} />
                    </>
                  )}
                </>
              }
              name="totalFatorTitulo"
            >
              <NumericFormat
                className="ant-input noBoderColor number-input"
                style={{ width: "100%" }}
                thousandSeparator="."
                decimalSeparator=","
                decimalScale={2}
                prefix="R$"
                disabled
                fixedDecimalScale={true}
              />
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={12}>
          <Col span={6}>
            <Form.Item
              label="1º Prêmio"
              name="valorPremio1"
              rules={[
                {
                  required: true,
                  message: "Digite o valor do prêmio 1",
                },
              ]}
            >
              <NumericFormat
                className="ant-input noBoderColor number-input"
                style={{ width: "100%" }}
                thousandSeparator="."
                decimalSeparator=","
                decimalScale={2}
                prefix="R$"
                fixedDecimalScale={true}
                disabled={!addGrade}
                onValueChange={() =>
                  setTimeout(() => {
                    form.setFieldValue("valorPremio1", realParaNumber(form.getFieldValue("valorPremio1")));
                    handleCalculoValorTotal();
                  }, 700)
                }
              />
            </Form.Item>
          </Col>
          <Col span={6}>
            <Form.Item
              label="2º Prêmio"
              name="valorPremio2"
              rules={[
                {
                  required: true,
                  message: "Digite o valor do prêmio 2",
                },
              ]}
            >
              <NumericFormat
                className="ant-input noBoderColor number-input"
                style={{ width: "100%" }}
                thousandSeparator="."
                decimalSeparator=","
                decimalScale={2}
                prefix="R$"
                fixedDecimalScale={true}
                disabled={!addGrade}
                onValueChange={() =>
                  setTimeout(() => {
                    form.setFieldValue("valorPremio2", realParaNumber(form.getFieldValue("valorPremio2")));
                    handleCalculoValorTotal();
                  }, 700)
                }
              />
            </Form.Item>
          </Col>
          <Col span={6}>
            <Form.Item
              label="3º Prêmio"
              name="valorPremio3"
              rules={[
                {
                  required: true,
                  message: "Digite o valor do prêmio 3",
                },
              ]}
            >
              <NumericFormat
                className="ant-input noBoderColor number-input"
                style={{ width: "100%" }}
                thousandSeparator="."
                decimalSeparator=","
                decimalScale={2}
                prefix="R$"
                fixedDecimalScale={true}
                disabled={!addGrade}
                onValueChange={() =>
                  setTimeout(() => {
                    form.setFieldValue("valorPremio3", realParaNumber(form.getFieldValue("valorPremio3")));
                    handleCalculoValorTotal();
                  }, 700)
                }
              />
            </Form.Item>
          </Col>
          <Col span={6}>
            <Form.Item
              label="4º Prêmio"
              name="valorPremio4"
              rules={[
                {
                  required: true,
                  message: "Digite o valor do prêmio 4",
                },
              ]}
            >
              <NumericFormat
                className="ant-input noBoderColor number-input"
                style={{ width: "100%" }}
                thousandSeparator="."
                decimalSeparator=","
                decimalScale={2}
                prefix="R$"
                fixedDecimalScale={true}
                disabled={!addGrade}
                onValueChange={() =>
                  setTimeout(() => {
                    form.setFieldValue("valorPremio4", realParaNumber(form.getFieldValue("valorPremio4")));
                    handleCalculoValorTotal();
                  }, 700)
                }
              />
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={12}>
          <Col span={6}>
            <Form.Item
              label="5º Prêmio"
              name="valorPremio5"
              rules={[
                {
                  required: true,
                  message: "Digite o valor do prêmio 5",
                },
              ]}
            >
              <NumericFormat
                className="ant-input noBoderColor number-input"
                style={{ width: "100%" }}
                thousandSeparator="."
                decimalSeparator=","
                decimalScale={2}
                prefix="R$"
                fixedDecimalScale={true}
                disabled={!addGrade}
                onValueChange={() =>
                  setTimeout(() => {
                    form.setFieldValue("valorPremio5", realParaNumber(form.getFieldValue("valorPremio5")));
                    handleCalculoValorTotal();
                  }, 700)
                }
              />
            </Form.Item>
          </Col>
          <Col span={6}>
            <Form.Item
              label="Rodada"
              name="valorRodada"
              rules={[
                {
                  required: true,
                  message: "Digite o valor da rodada",
                },
              ]}
            >
              <NumericFormat
                className="ant-input noBoderColor number-input"
                style={{ width: "100%" }}
                thousandSeparator="."
                decimalSeparator=","
                decimalScale={2}
                prefix="R$"
                fixedDecimalScale={true}
                disabled={!addGrade}
                onValueChange={() =>
                  setTimeout(() => {
                    form.setFieldValue("valorRodada", realParaNumber(form.getFieldValue("valorRodada")));
                    handleCalculoValorTotal();
                  }, 700)
                }
              />
            </Form.Item>
          </Col>
          <Col span={6}>
            <Form.Item
              label="Premiação Líquida"
              name="valorTotalPremiacaoLiquido"
              rules={[
                {
                  required: true,
                  message: "Digite o total liquido",
                },
              ]}
            >
              <NumericFormat
                className="ant-input noBoderColor number-input"
                style={{ width: "100%" }}
                thousandSeparator="."
                decimalSeparator=","
                decimalScale={2}
                prefix="R$"
                fixedDecimalScale={true}
                disabled
                onChange={() => form.setFieldValue("valorTotalPremiacaoLiquido", realParaNumber(form.getFieldValue("valorTotalPremiacaoLiquido")))}
              />
            </Form.Item>
          </Col>
          <Col span={6}>
            <Form.Item
              label={
                <>
                  <span style={{ marginRight: "3px" }}>Premiação Bruta</span>
                  {!(Math.abs(Math.abs(totalValor) - Math.abs(totalFatorTitulo)) <= 0.01) ? (
                    <>
                      <Tooltip title="O valor da premiação bruta difere do fator x título">
                        <WarningOutlined style={{ color: "#FF0000" }} />
                      </Tooltip>
                    </>
                  ) : (
                    <>
                      <CheckCircleOutlined style={{ color: "#008000", marginLeft: "3px" }} />
                    </>
                  )}
                </>
              }
              name="valorTotalPremiacaoBruto"
              rules={[
                {
                  required: true,
                  message: "Digite o total bruto",
                },
              ]}
            >
              <NumericFormat
                className="ant-input noBoderColor number-input"
                style={{ width: "100%" }}
                thousandSeparator="."
                decimalSeparator=","
                decimalScale={2}
                prefix="R$"
                fixedDecimalScale={true}
                disabled
                onChange={() => form.setFieldValue("valorTotalPremiacaoBruto", realParaNumber(form.getFieldValue("valorTotalPremiacaoBruto")))}
              />
            </Form.Item>
          </Col>
        </Row>
        <Divider />
        <Row gutter={12}>
          <Col span={24} className="modalFooter">
            <Space size="middle">
              <Button className="cancelButton" onClick={voltar} icon={<LeftOutlined />}>
                VOLTAR
              </Button>
              <Button htmlType="submit" icon={<CheckOutlined />} className="buttonPrimary">
                {editarSorteio ? "ATUALIZAR" : "SALVAR"}
              </Button>
            </Space>
          </Col>
        </Row>
      </Form>
    </Flex>
  );
};

export default CadastrarPage;
